import { useStore } from "vuex";
import { watch } from "vue";
import { setCssVars } from "@/v2/paletteGenerator/index.js";
import chroma from "chroma-js";
import { SiteConfigSettings } from "@/types/siteConfig.ts";

interface InitializeThemeParams {
  primaryColor: string;
  primaryColorDark: string;
  primaryColorLight: string;
  darkMode?: boolean;
}

interface InitializeFontParams {
  font: string;
  font2: string;
  font3: string;
}

function convertToRgbArray(color: string) {
  try {
    return chroma(color).rgb();
  } catch {
    return [0, 0, 0];
  }
}

export const customColorMap = new Map([
  ["titleColor", "--header-default-text"],
  ["startColor", "--home-navigation-button-text"],
  ["backgroundColor", "--background-stylized-color"],
  ["headerStripeColor", "--header-default-divider"],
  ["homeButtonsColor", "--home-button-text"],
  ["categoryLogoCardHeaderColor", "--card-inverted-logo-surface"],
  ["categoryLogoCardText", "--category-logo-card-text"],
  ["categoryLogoCardStripe", "--card-inverted-divider"],
  ["categoryLogoCardBackground", "--category-logo-card-background"],
  ["categoryTextColor", "--card-default-text"],
  ["pillCategoryTextColor", "--category-pill-card-text"],
  ["personStripeTextColor", "--profile-card-default-text"],
  ["bannerTitleColor", "--pennant-default-category-text"],
  ["bannerHeaderBackgroundColor", "--pennant-default-category-surface"],
  ["bannerStripeColor", "--pennant-default-divider"],
  ["bannerBodyBackgroundColor", "--pennant-default-surface"],
  ["bannerBodyTextColor", "--pennant-default-text"],
]);

export function setBodyDataTheme(darkMode: boolean) {
  document
    .getElementsByTagName("body")[0]
    .setAttribute("data-theme", darkMode ? "Dark" : "Light");
}

export default function useThemeInitializer() {
  const store = useStore();

  function initializeTheme({
    primaryColor,
    primaryColorDark,
    primaryColorLight,
    darkMode = false,
  }: InitializeThemeParams) {
    const shades = [
      {
        token: "neutral",
        rgb: convertToRgbArray(primaryColor),
      },
      {
        token: "light",
        rgb: convertToRgbArray(primaryColorLight),
      },
      {
        token: "dark",
        rgb: convertToRgbArray(primaryColorDark),
      },
    ];

    setCssVars(shades);

    setBodyDataTheme(darkMode);
  }

  function initializeCustomFonts({ font, font2, font3 }: InitializeFontParams) {
    const defaultFont = "var(--font-family-sans-serif)";

    document.documentElement.style.setProperty(
      "--font-family-custom-1",
      font ?? defaultFont
    );

    document.documentElement.style.setProperty(
      "--font-family-custom-2",
      font2 ?? defaultFont
    );

    document.documentElement.style.setProperty(
      "--font-family-custom-3",
      font3 ?? defaultFont
    );
  }

  function initializeCustomColors(
    colorSettings: SiteConfigSettings["styling"]["color"]
  ) {
    if (colorSettings.enableCustomColors) {
      Object.entries(colorSettings).forEach(([key, value]) => {
        if (!customColorMap.has(key) || !value || typeof value === "boolean") {
          return;
        }

        // Must use `body` instead of `documentElement` to override color variables
        document.body.style.setProperty(
          customColorMap.get(key)!,
          value,
          "important"
        );
      });
    } else {
      Object.keys(colorSettings).forEach((key) => {
        document.body.style.removeProperty(customColorMap.get(key)!);
      });
    }
  }

  watch(
    () => store.getters.getStoreType("Settings", "styling", "color"),
    (colorSettings: SiteConfigSettings["styling"]["color"]) => {
      initializeTheme({
        primaryColor: colorSettings.primaryColor,
        primaryColorDark: colorSettings.primaryColorDark,
        primaryColorLight: colorSettings.primaryColorLight,
        darkMode: colorSettings.darkMode,
      });

      initializeCustomColors(colorSettings);
    },
    {
      deep: true,
      immediate: true,
    }
  );

  watch(
    () => store.getters.getStoreType("Settings", "styling", "text"),
    (fontSettings) => {
      // if the font is a custom font filename, we need to strip the extension
      initializeCustomFonts({
        font: fontSettings.font.split(".")[0],
        font2: fontSettings.font2.split(".")[0],
        font3: fontSettings.font3.split(".")[0],
      });
    },
    {
      deep: true,
      immediate: true,
    }
  );
}
